import { convertTimestampsToDates, FirebaseProperties } from '@/types/Helper/FirebaseProperties'
import {
  CollectionReference,
  doc,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  type DocumentData,
  type QueryDocumentSnapshot,
  type SnapshotOptions
} from 'firebase/firestore'
import type { ObjectSummary } from '@/types/Helper/Interfaces'
import { createEventDetails, type EventDetails } from './EventInterfaces'
import { Resource } from '@/types/Helper/Enums'
import { FirebaseCollections } from '@/utils/firestore/FirestorePaths'

type ScheduledResourceType = Resource.ac | Resource.room | Resource.ins

export type ScheduledEventStatus = 'active' | 'expired'
export class ScheduledEvent extends FirebaseProperties {
  eventDetail: EventDetails = createEventDetails()
  comment = ''
  resource: ObjectSummary | null = null
  resourceType: ScheduledResourceType = Resource.ac
  people: string[] = []
  checkedOutTime: Date | null = null
  checkedOutBy: string | null = null
  // Corrected starting reading captured at checkout when the pilot found
  // the meter didn't match the stored value. null = no correction (check-in
  // falls back to maintenanceToDate as before). Consumed by check-in as its
  // start reading; the resulting mismatch with the prior check-in's ending
  // surfaces the missing session in AircraftCheckLogReport automatically.
  startHobbsOverride: number | null = null
  startTachOverride: number | null = null
  checkedInTime: Date | null = null
  checkedInBy: string | null = null
  // 'active' by default; the no-show sweep sets 'expired' when a reservation
  // passes its start + grace window without being checked out. Existing docs
  // have no status field, so they read as 'active' via the ?? below.
  status: ScheduledEventStatus = 'active'
  oilAdded = 0
  fuelAdded = 0
  isCancelled = false
  cancellationReason = ''
  instructor: ObjectSummary | null = null
  destination = ''
  pilotInCommand: ObjectSummary | null = null

  constructor(data?: Partial<ScheduledEvent>) {
    super()
    Object.assign(this, data)
  }

  setStore(storeId: string) {
    const basePath = `${FirebaseCollections.Stores}/${storeId}/${FirebaseCollections.Events}`

    this.thisCollection = basePath

    const id = this.firebaseInfo?.__id
    this.thisDocument = id ? `${basePath}/${id}` : basePath
  }

  getSummary(): ObjectSummary {
    return {
      id: this.firebaseInfo.__id,
      name: this.eventDetail.title,
      active: true
    }
  }

  getDetails(): string {
    const title = this.eventDetail.title ?? ''
    const startDate = this.eventDetail.startDate

    if (!(startDate instanceof Date)) return title

    return `${title} ${startDate.toLocaleDateString()} ${startDate.toLocaleTimeString()}`
  }

  async cancelEvent(coll: CollectionReference, updatedBy: string) {
    const id = this.firebaseInfo?.__id
    if (!id || id === '-1') return

    const eventRef = doc(coll, id).withConverter(null)
    const updated = await runTransaction(coll.firestore, async (transaction) => {
      const snapshot = await transaction.get(eventRef)
      if (!snapshot.exists()) throw new Error('This event no longer exists.')
      const savedEvent = snapshot.data()
      if (savedEvent.checkedOutTime != null) {
        throw new Error('An event that has already been checked out cannot be cancelled.')
      }
      if (savedEvent.isCancelled) return false

      transaction.update(eventRef, {
        isCancelled: true,
        'firebaseInfo._updatedAt': serverTimestamp(),
        'firebaseInfo._updatedBy': updatedBy
      })
      return true
    })

    if (updated) {
      this.firebaseInfo._updatedAt = Timestamp.now()
      this.firebaseInfo._updatedBy = updatedBy
    }
    this.isCancelled = true
  }

  updateEvent(coll: CollectionReference, updatedBy: string) {
    this.firebaseInfo._updatedAt = Timestamp.now()
    this.firebaseInfo._updatedBy = updatedBy
    return setDoc(doc(coll, this.firebaseInfo.__id), this, { merge: true })
  }

  getName(): string {
    return this.eventDetail.title
  }
}

export const ScheduledEventConverter = {
  toFirestore: (event: ScheduledEvent) => {
    const firebaseInfo = {
      ...event.firebaseInfo,
      _updatedAt: Timestamp.now()
    }

    return {
      eventDetail: event.eventDetail,
      comment: event.comment,
      resource: event.resource,
      resourceType: event.resourceType,
      people: event.people,
      checkedOutTime: event.checkedOutTime,
      checkedOutBy: event.checkedOutBy,
      startHobbsOverride: event.startHobbsOverride ?? null,
      startTachOverride: event.startTachOverride ?? null,
      checkedInTime: event.checkedInTime,
      checkedInBy: event.checkedInBy,
      status: event.status,
      oilAdded: event.oilAdded,
      fuelAdded: event.fuelAdded,
      isCancelled: event.isCancelled,
      cancellationReason: event.cancellationReason,
      instructor: event.instructor,
      destination: event.destination,
      pilotInCommand: event.pilotInCommand,
      firebaseInfo
    }
  },

  fromFirestore: (
    snapshot: QueryDocumentSnapshot<DocumentData, DocumentData>,
    options?: SnapshotOptions
  ) => {
    const data = snapshot.data(options)
    const converted = convertTimestampsToDates(data)
    return new ScheduledEvent({
      ...converted,
      status: (converted.status as ScheduledEventStatus) ?? 'active'
    })
  }
}
